import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import estimateProvider from "@/providers/estimate.provider";
import type { IXactimateToolRowData } from "@/interfaces/payloads/xactimate";
import { ESTIMATE_KEYS } from "@/hooks/queries/estimates/queryKeys";
import type { GetEstimateDetailData } from "@/hooks/queries/estimates/useGetEstimateDetailQuery";
import { useStartXactimatePhotoUploadMutation } from "./useStartXactimatePhotoUploadMutation";

export interface ExportToXactimateMutationParams {
  estimateId: string;
  fixedMapping?: IXactimateToolRowData[];
}

export type ExportResult = { status: "exported"; transactionId: string };

export function useExportToXactimateMutation(options?: { onExportSuccess?: () => void }) {
  const queryClient = useQueryClient();
  const { mutate: startPhotoUpload } = useStartXactimatePhotoUploadMutation();

  return useMutation({
    mutationFn: async (params: ExportToXactimateMutationParams): Promise<ExportResult> => {
      const { estimateId, fixedMapping } = params;

      toast.info("Exporting Project to Xactimate…", {
        className: "!text-blue-500 !bg-blue-500/10 !border-blue-500",
      });

      const encodedMappings = btoa(JSON.stringify(fixedMapping ?? []));
      const urlSafeEncodedMappings = encodeURIComponent(encodedMappings);

      const { data: transactionId } = await estimateProvider.exportToXactimate(
        { estimateId },
        { fixedMappingBody: urlSafeEncodedMappings },
      );

      if (typeof transactionId !== "string" || !transactionId) {
        throw new Error("Export failed: no transaction ID returned");
      }

      return { status: "exported", transactionId };
    },
    onError: (error) => {
      toast.error("Error exporting to Xactimate", {
        className: "!text-red-500 !bg-red-500/10 !border-red-500",
      });
      console.error("Export to Xactimate error:", error);
    },
    onSuccess: (result, variables) => {
      queryClient.setQueryData<GetEstimateDetailData>(ESTIMATE_KEYS.detail(variables.estimateId), (old) => {
        if (!old) return old;
        return { ...old, exportedToXactimate: true };
      });
      options?.onExportSuccess?.();
      toast.success("Successfully exported to Xactimate!", {
        className: "!text-green-500 !bg-green-500/10 !border-green-500",
      });

      startPhotoUpload({ estimateId: variables.estimateId, transactionId: result.transactionId });
    },
  });
}
